import { useState, type ReactNode } from 'react';
import { Button, Field, Input, Modal } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EFFECTIVE_FROM_LABEL, todayIsoUtc } from '@/lib/account-lead-history';

/*
 * AccountLeadChangeDialog: the "Effective from" question asked whenever a
 * company's account lead is changed (owner decision 4, 2026-10-25).
 *
 * Default today (UTC, the server's clock for `ACCOUNT_LEAD_EFFECTIVE_FROM_IN_FUTURE`),
 * may be backdated, never in the future. The caller sends the chosen date as
 * `accountLeadEffectiveFrom` alongside `accountLeadId` and owns the request,
 * `busy` and `error`. The body mounts only while open, so every open starts
 * from today again.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A lead as callers hold it: a user summary, a bare name, or nobody. */
export type AccountLeadChangeLead = { name: string } | string | null | undefined;

export interface AccountLeadChangeDialogProps {
  open: boolean;
  companyName: string;
  /** The lead being replaced; null = none. */
  fromLead: AccountLeadChangeLead;
  /** The new lead; null = removing the lead. */
  toLead: AccountLeadChangeLead;
  onConfirm: (dateIso: string) => void;
  onClose: () => void;
  busy?: boolean;
  error?: ReactNode;
}

function leadName(lead: AccountLeadChangeLead): string {
  if (lead === null || lead === undefined) return 'No account lead';
  const name = typeof lead === 'string' ? lead : lead.name;
  return name.trim() === '' ? 'No account lead' : name;
}

export function AccountLeadChangeDialog(props: AccountLeadChangeDialogProps) {
  if (!props.open) return null;
  return <DialogBody {...props} />;
}

function DialogBody({
  companyName,
  fromLead,
  toLead,
  onConfirm,
  onClose,
  busy = false,
  error,
}: AccountLeadChangeDialogProps) {
  const [today] = useState(() => todayIsoUtc());
  const [effectiveFrom, setEffectiveFrom] = useState(today);

  const wellFormed = ISO_DATE.test(effectiveFrom);
  const inFuture = wellFormed && effectiveFrom > today;

  let blockedBy: string | null = null;
  if (effectiveFrom === '') blockedBy = `Choose the ${EFFECTIVE_FROM_LABEL.toLowerCase()} date`;
  else if (!wellFormed) blockedBy = 'Enter a valid date';
  else if (inFuture) blockedBy = 'The date cannot be in the future';

  const dateError = inFuture ? 'Choose today or an earlier date.' : undefined;

  const close = () => {
    if (!busy) onClose();
  };

  const confirm = () => {
    if (blockedBy !== null || busy) return;
    onConfirm(effectiveFrom);
  };

  return (
    <Modal
      open
      onClose={close}
      title={`Change account lead · ${companyName}`}
      size="sm"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirm}
            loading={busy}
            disabled={blockedBy !== null}
            tooltip={blockedBy ?? undefined}
          >
            Change lead
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          confirm();
        }}
      >
        <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
          <dt className="text-fg-muted">From</dt>
          <dd className="m-0 min-w-0 [overflow-wrap:anywhere] text-fg">{leadName(fromLead)}</dd>
          <dt className="text-fg-muted">To</dt>
          <dd className="m-0 min-w-0 [overflow-wrap:anywhere] font-medium text-fg">{leadName(toLead)}</dd>
        </dl>

        <Field label={EFFECTIVE_FROM_LABEL} required error={dateError}>
          {(fieldProps) => (
            <Input
              {...fieldProps}
              type="date"
              value={effectiveFrom}
              max={today}
              required
              disabled={busy}
              invalid={dateError !== undefined}
              onChange={(event) => setEffectiveFrom(event.target.value)}
            />
          )}
        </Field>

        {error ? <Notice tone="danger">{error}</Notice> : null}
      </form>
    </Modal>
  );
}
